import React, { useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Remove as NeutralIcon,
} from '@mui/icons-material';
import { WidgetProps } from '../types/index.js';

interface StockAnalysis {
  symbol: string;
  price: number;
  volume: number;
  sentiment?: string;
  summary?: string;
  keyPoints?: string[];
}

interface ComparisonMetric {
  name: string;
  stock1Value: any;
  stock2Value: any;
  stock3Value?: any;
  comparison: 'better' | 'worse' | 'neutral';
}

export const PortfolioComparisonWidget: React.FC<WidgetProps> = ({
  widget,
  onUpdateOutputs,
}) => {
  // Get connected stock analyses
  const stock1 = widget.inputs.find(input => input.id === 'stock1')?.value as StockAnalysis | undefined;
  const stock2 = widget.inputs.find(input => input.id === 'stock2')?.value as StockAnalysis | undefined;
  const stock3 = widget.inputs.find(input => input.id === 'stock3')?.value as StockAnalysis | undefined;

  // Calculate comparison metrics
  const metrics = useMemo(() => {
    if (!stock1 || !stock2) return [];

    const results: ComparisonMetric[] = [];

    // Price Performance (normalized to percentage)
    const pricePerf1 = ((stock1.price - 100) / 100) * 100; // Assume 100 as baseline
    const pricePerf2 = ((stock2.price - 100) / 100) * 100;
    results.push({
      name: 'Price Performance',
      stock1Value: `${pricePerf1.toFixed(2)}%`,
      stock2Value: `${pricePerf2.toFixed(2)}%`,
      stock3Value: stock3 ? `${((stock3.price - 100) / 100 * 100).toFixed(2)}%` : undefined,
      comparison: pricePerf1 > pricePerf2 ? 'better' : pricePerf1 < pricePerf2 ? 'worse' : 'neutral',
    });

    // Volume (in millions)
    results.push({
      name: 'Trading Volume',
      stock1Value: `${(stock1.volume / 1000000).toFixed(2)}M`,
      stock2Value: `${(stock2.volume / 1000000).toFixed(2)}M`,
      stock3Value: stock3 ? `${(stock3.volume / 1000000).toFixed(2)}M` : undefined,
      comparison: stock1.volume > stock2.volume ? 'better' : 'worse',
    });

    // Sentiment Score
    const sentimentScore = (sentiment?: string) => {
      switch (sentiment) {
        case 'bullish': return 3;
        case 'neutral': return 2;
        case 'bearish': return 1;
        default: return 0;
      }
    };
    const sent1 = sentimentScore(stock1.sentiment);
    const sent2 = sentimentScore(stock2.sentiment);
    results.push({
      name: 'AI Sentiment',
      stock1Value: stock1.sentiment || 'N/A',
      stock2Value: stock2.sentiment || 'N/A',
      stock3Value: stock3?.sentiment || 'N/A',
      comparison: sent1 > sent2 ? 'better' : sent1 < sent2 ? 'worse' : 'neutral',
    });

    // Volatility (simulated based on price)
    const volatility1 = Math.abs(stock1.price - 150) / 150 * 100;
    const volatility2 = Math.abs(stock2.price - 150) / 150 * 100;
    results.push({
      name: 'Volatility',
      stock1Value: `${volatility1.toFixed(2)}%`,
      stock2Value: `${volatility2.toFixed(2)}%`,
      stock3Value: stock3 ? `${(Math.abs(stock3.price - 150) / 150 * 100).toFixed(2)}%` : undefined,
      comparison: volatility1 < volatility2 ? 'better' : 'worse', // Lower is better
    });

    // P/E Ratio (simulated)
    const pe1 = stock1.price / 5; // Simplified P/E
    const pe2 = stock2.price / 5;
    results.push({
      name: 'P/E Ratio',
      stock1Value: pe1.toFixed(2),
      stock2Value: pe2.toFixed(2),
      stock3Value: stock3 ? (stock3.price / 5).toFixed(2) : undefined,
      comparison: pe1 < pe2 ? 'better' : 'worse', // Lower P/E often better
    });

    return results;
  }, [stock1, stock2, stock3]);

  // Calculate portfolio score
  const portfolioScore = useMemo(() => {
    if (!stock1 || !stock2) return null;

    const stocks = [stock1, stock2];
    if (stock3) stocks.push(stock3);

    // Simple diversification score based on price correlation
    const prices = stocks.map(s => s.price);
    const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
    const variance = prices.reduce((sum, price) => sum + Math.pow(price - avgPrice, 2), 0) / prices.length;
    const diversificationScore = Math.min(variance / 100, 1) * 100;

    // Sentiment alignment
    const sentiments = stocks.map(s => s.sentiment);
    const bullishCount = sentiments.filter(s => s === 'bullish').length;
    const sentimentScore = (bullishCount / stocks.length) * 100;

    return {
      diversification: diversificationScore,
      sentiment: sentimentScore,
      overall: (diversificationScore + sentimentScore) / 2,
    };
  }, [stock1, stock2, stock3]);

  // Use ref to track if we've already sent outputs for current data
  const lastOutputRef = useRef<string>('');

  // Update outputs only when inputs change
  useEffect(() => {
    if (!stock1 || !stock2) return;

    // Create a stable key from input data
    const outputKey = JSON.stringify({
      s1: stock1.symbol,
      s1p: stock1.price,
      s1v: stock1.volume,
      s1s: stock1.sentiment,
      s2: stock2.symbol,
      s2p: stock2.price,
      s2v: stock2.volume,
      s2s: stock2.sentiment,
      s3: stock3?.symbol,
      s3p: stock3?.price,
      s3v: stock3?.volume,
      s3s: stock3?.sentiment,
    });

    // Only update if the key changed
    if (outputKey !== lastOutputRef.current) {
      lastOutputRef.current = outputKey;
      onUpdateOutputs({
        comparisonData: metrics,
        portfolioScore,
        stocks: [stock1, stock2, ...(stock3 ? [stock3] : [])],
        portfolioData: {
          stocks: [stock1, stock2, ...(stock3 ? [stock3] : [])],
          portfolioScore,
        },
      });
    }
  }, [stock1, stock2, stock3, metrics, portfolioScore, onUpdateOutputs]);

  if (!stock1 || !stock2) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Alert severity="info">
          Connect at least 2 stock analyses to compare portfolio options
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="h6">Portfolio Comparison</Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Chip label={stock1.symbol} color="primary" size="small" />
          <Chip label={stock2.symbol} color="secondary" size="small" />
          {stock3 && <Chip label={stock3.symbol} color="default" size="small" />}
        </Box>
      </Box>

      {/* Metrics Table */}
      <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Metric</TableCell>
                <TableCell align="center">{stock1.symbol}</TableCell>
                <TableCell align="center">{stock2.symbol}</TableCell>
                {stock3 && <TableCell align="center">{stock3.symbol}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {metrics.map((metric) => (
                <TableRow key={metric.name}>
                  <TableCell component="th" scope="row">
                    {metric.name}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      {metric.stock1Value}
                      {metric.comparison === 'better' && <TrendingUp color="success" fontSize="small" />}
                      {metric.comparison === 'worse' && <TrendingDown color="error" fontSize="small" />}
                      {metric.comparison === 'neutral' && <NeutralIcon color="disabled" fontSize="small" />}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      {metric.stock2Value}
                      {metric.comparison === 'worse' && <TrendingUp color="success" fontSize="small" />}
                      {metric.comparison === 'better' && <TrendingDown color="error" fontSize="small" />}
                      {metric.comparison === 'neutral' && <NeutralIcon color="disabled" fontSize="small" />}
                    </Box>
                  </TableCell>
                  {stock3 && (
                    <TableCell align="center">{metric.stock3Value}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Portfolio Score */}
        {portfolioScore && (
          <Paper sx={{ p: 2, mt: 2 }} variant="outlined">
            <Typography variant="subtitle2" gutterBottom>
              Portfolio Analysis
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Diversification Score</Typography>
                <Typography variant="body2">{portfolioScore.diversification.toFixed(0)}%</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={portfolioScore.diversification} 
                color={portfolioScore.diversification > 70 ? 'success' : 'warning'}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Sentiment Alignment</Typography>
                <Typography variant="body2">{portfolioScore.sentiment.toFixed(0)}%</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={portfolioScore.sentiment}
                color={portfolioScore.sentiment > 60 ? 'success' : 'warning'}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2">Overall Score</Typography>
              <Chip 
                label={`${portfolioScore.overall.toFixed(0)}%`}
                color={portfolioScore.overall > 70 ? 'success' : portfolioScore.overall > 50 ? 'warning' : 'error'}
              />
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};